import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { BookOpen, FileText, Newspaper, Award } from 'lucide-react'
import type { RecentItem } from '../../types/dashboard.types'

interface RecentItemsProps {
    items: RecentItem[]
    loading?: boolean
}

const RecentItems = ({ items, loading }: RecentItemsProps) => {
    const getIcon = (type: RecentItem['type']) => {
        switch (type) {
            case 'aula':
                return <BookOpen className="w-5 h-5" />
            case 'teste':
                return <FileText className="w-5 h-5" />
            case 'noticia':
                return <Newspaper className="w-5 h-5" />
            case 'resultado':
                return <Award className="w-5 h-5" />
            default:
                return <FileText className="w-5 h-5" />
        }
    }

    const getTypeLabel = (type: RecentItem['type']) => {
        const labels: Record<RecentItem['type'], string> = {
            aula: 'Aula',
            teste: 'Teste',
            noticia: 'Notícia',
            resultado: 'Resultado'
        }
        return labels[type]
    }

    const getColorClasses = (type: RecentItem['type']) => {
        const colors: Record<RecentItem['type'], string> = {
            aula: 'bg-purple-100 text-purple-600',
            teste: 'bg-blue-100 text-blue-600',
            noticia: 'bg-green-100 text-green-600',
            resultado: 'bg-yellow-100 text-yellow-600'
        }
        return colors[type]
    }

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-bizu p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Itens Recentes</h3>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (items.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-bizu p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Itens Recentes</h3>
                <p className="text-gray-500 text-center py-8">Nenhum item recente</p>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-bizu p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Itens Recentes</h3>
            <div className="space-y-3">
                {items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                        <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(item.type)}`}>
                                {getIcon(item.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium text-gray-500 uppercase">{getTypeLabel(item.type)}</span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500">
                                        {formatDistanceToNow(new Date(item.timestamp), {
                                            addSuffix: true,
                                            locale: ptBR
                                        })}
                                    </span>
                                </div>
                                <h4 className="text-sm font-medium text-gray-900 truncate">{item.title}</h4>
                                {item.description && (
                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                                )}
                                {item.author && (
                                    <p className="text-xs text-gray-500 mt-1">Por: {item.author}</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default RecentItems
